import { Global } from '@emotion/react';
import { PolestarApp } from '@polestar/component-warehouse-react';
import {
  CopyCredentials,
  PageErrorBoundary,
  type RouteDefinition,
  useAuth,
  useMarket
} from '@polestar/shared-front-myorder';
import { type ErrorInfo, type ReactNode, Suspense, lazy } from 'react';
import { config } from '~root/src/config';
import { routes } from '~routes/definition';
import { AppRouter } from './AppRouter';
import { AppPage } from './components/AppPage';
import { globalStyles } from './global.styles';

const views = {
  UnauthorizedView: lazy(() => import('./views/UnauthorizedView')),
  NotFoundView: lazy(() => import('./views/NotFoundView')),
  OrdersView: lazy(() => import('./views/OrdersView')),
  ExtrasView: lazy(() => import('./views/ExtrasView')),
  DeliveredView: lazy(() => import('./views/DeliveredView'))
};

type View = keyof typeof views;

type RouteViewProps = {
  view: View;
};
const RouteView = ({ view }: RouteViewProps) => {
  const ViewComponent = views[view];

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ViewComponent />
    </Suspense>
  );
};

type RouterProps = {
  loadingAuth: boolean;
  isAuthenticated: boolean;
  children?: ReactNode;
};

const Router = ({ loadingAuth, isAuthenticated }: RouterProps) => {
  const getView = (routeDefinition: RouteDefinition | null) => {
    const view = (
      isAuthenticated ? routeDefinition?.view || 'NotFoundView' : 'UnauthorizedView'
    ) as View;

    return (
      <AppPage hideSideNavigation={view === 'ExtrasView' || view === 'DeliveredView'}>
        <RouteView view={view} />
      </AppPage>
    );
  };

  const getNotFoundView = () => <RouteView view="NotFoundView" />;

  if (loadingAuth) {
    return null;
  }

  return <AppRouter routes={routes} getView={getView} getNotFoundView={getNotFoundView} />;
};

const onError = (error: unknown, info: ErrorInfo) => {
  console.error(
    `Error: ${error instanceof Error ? error.message : 'Unknown error'}, Component Stack: ${info.componentStack}`
  );
};

export const App = () => {
  const { currentMarket } = useMarket();

  const { isAuthenticated, isLoading } = useAuth();
  return (
    <PolestarApp rootSelector="#root">
      <Global styles={globalStyles} />
      <PageErrorBoundary onError={onError}>
        {currentMarket && <Router loadingAuth={isLoading} isAuthenticated={isAuthenticated} />}
      </PageErrorBoundary>
      <CopyCredentials isProd={config.isProduction} />
    </PolestarApp>
  );
};
